import { useRef, useState, type DragEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { documentsApi } from '../../api/documents'
import { RefusedError } from '../../api/client'
import type { DocumentOrigin, DocumentTargets, DocumentUploaded, DocumentView } from '../../types/documents'
import { LineIcon } from '../LineIcon'
import { DOCUMENT_ACCEPT, DOCUMENT_HINT, formatBytes } from './documents'
import './documents.css'

// The drop zone (REQ-119): "Add a document" or drop one on it. The file goes up once, attached to what the
// caller names (a person, an engagement, a company), with where it was offered from; the API reads it and
// starts the job that draws facts. A refusal (the kind, the size, an unknown target) is shown here, in words.

const why = (e: unknown) => (e instanceof RefusedError ? e.refusal.message : e instanceof Error ? e.message : String(e))

export function DocumentDrop({ spoke, targets, origin, compact = false, onUploaded }: {
  spoke: string
  targets: DocumentTargets
  origin: DocumentOrigin
  /** one line, for a side panel */
  compact?: boolean
  onUploaded?: (view: DocumentView, jobId: string | null) => void
}) {
  const qc = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const [fileName, setFileName] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const upload = useMutation({
    mutationFn: (file: File) => documentsApi.upload(spoke, file, { ...targets, origin }, setProgress),
    onSuccess: (r: DocumentUploaded) => {
      void qc.invalidateQueries({ queryKey: ['documents', spoke] })
      onUploaded?.(r.document, r.jobId)
    },
  })
  const pick = (file?: File | null) => {
    if (!file) return
    setFileName(file.name)
    setProgress(0)
    upload.mutate(file)
  }
  const drop = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files?.[0]) }
  const busy = upload.isPending

  return (
    <div className={`document-drop${compact ? ' compact' : ''}${over ? ' over' : ''}${busy ? ' busy' : ''}`}
      onDragOver={(e) => { e.preventDefault(); if (!over) setOver(true) }} onDragEnter={(e) => { e.preventDefault(); setOver(true) }}
      onDragLeave={() => setOver(false)} onDrop={drop} aria-busy={busy}>
      <input ref={inputRef} type="file" accept={DOCUMENT_ACCEPT} hidden onChange={(e) => { pick(e.target.files?.[0]); e.currentTarget.value = '' }} />
      <div className="document-drop-line">
        <button type="button" className="button small icon" onClick={() => inputRef.current?.click()} disabled={busy}>
          <LineIcon name="upload" size={13} />{busy ? `Uploading ${fileName ?? ''}… ${Math.round(progress * 100)}%` : 'Add a document'}
        </button>
        <span className="muted small">{busy ? 'It is read as it lands; the facts follow.' : compact ? 'or drop one here' : 'or drop a file here'}</span>
      </div>
      {busy && <progress className="document-progress" value={progress} max={1} aria-label={`Uploading ${fileName ?? 'the file'}`} />}
      {!compact && <p className="muted small document-kinds">{DOCUMENT_HINT}</p>}
      {upload.isError && <p className="error small" role="alert">{why(upload.error)}</p>}
      {upload.isSuccess && upload.data && !busy && (
        <p className="muted small document-landed" role="status">
          {upload.data.document.document.fileName} ({formatBytes(upload.data.document.document.bytes)}) is on file{upload.data.jobId ? ' and being read.' : '.'}
        </p>
      )}
    </div>
  )
}
